import { Badge } from "components/buttons/buttons.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { Text } from "components/Typography/text.styled";
import { ColorV2 } from "config/colors";
import { CompanyRole } from "model/User";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useAuthStore } from "stores/authStore/authStore";
import { useChatStore } from "web-apps/company/stores/chatStore/chatStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useTimeReportsV2Store } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store";
import { ChatContainerList } from "../chat/chatContainerList.component";
import {
  ActionsWrapper,
  ChatBlob,
  ChatIconWrapper,
  LoginLink,
  NavBarFrame,
  NavBarStyled,
  Spacer,
} from "./navBar.styled";

import { DropdownMenu } from "components/designV2/dropdownMenu/dropdownMenu";
import { NavbarMenu } from "components/designV2/navbarmenu/navbarMenu";
import { useSignOut } from "components/nav/hooks/useSignOut";
import { isSettingEnabled } from "model/settings.logic";
import { getDropdownMenuItems, getNavbarMenuItems } from "../nav-v2.mapper";
import { StampClockStaffRegisterExportModal } from "./stampClockStaffRegisterExportModal.component";

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Main navigation bar component.
 */
export const NavBar: React.FC<Props> = (props): React.ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const [authState] = useAuthStore();
  const [chatState] = useChatStore();
  const [timeReportsV2State] = useTimeReportsV2Store();

  const [companyState, companyActions] = useCompanyStore();
  const [missedMessagesQty, setMissedMessagesQty] = useState(0);
  const [isChatContainerVisible, setIsChatContainerVisible] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isStampClockStaffRegisterModalOpen, setIsStampClockStaffRegisterModalOpen] =
    useState(false);
  const signOut = useSignOut();

  const navbarItems = useMemo(
    () =>
      getNavbarMenuItems(
        authState.accountType,
        authState.user?.role,
        t,
        companyState.companySettings,
      ),
    [
      authState.accountType,
      authState.user?.role,
      t,
      companyState.companySettings,
    ],
  );

  const dropdownItems = useMemo(
    () =>
      getDropdownMenuItems(
        t,
        authState.accountType,
        authState.user?.role,
        companyState.companies,
        companyState.company,
        companyState.companySettings,
      ),
    [
      t,
      authState.accountType,
      authState.user?.role,
      companyState.companies,
      companyState.company,
      companyState.companySettings,
    ],
  );

  const navbarItemsWithBadges = useMemo(
    () =>
      navbarItems.map((item) =>
        item.value === "time-reports"
          ? { ...item, badgeCount: timeReportsV2State.count }
          : item,
      ),
    [navbarItems, timeReportsV2State.count],
  );

  const activeMenuValue = useMemo((): string => {
    const activeItem = navbarItemsWithBadges
      .filter((item) => pathname.startsWith(item.href))
      .sort((a, b) => b.href.length - a.href.length)[0];
    return activeItem?.value || "";
  }, [navbarItemsWithBadges, pathname]);

  const isStampClock = useMemo((): boolean => {
    return authState.user?.role === CompanyRole.COMPANY_TIMESTAMP;
  }, [authState.user?.role]);

  const hasChatModule = useMemo((): boolean => {
    return isSettingEnabled(
      companyState.companySettings,
      "CompanyShowChatModule",
    );
  }, [companyState.companySettings]);

  useEffect((): void => {
    const unreadMessagesCount = chatState.state.summary.chats.reduce(
      (previousValue: number, { unread }: { unread: number }) =>
        previousValue + unread,
      0,
    );
    setMissedMessagesQty(unreadMessagesCount);
  }, [chatState.state.summary]);

  return (
    <NavBarFrame $isStampClockRoute={isStampClock}>
      <NavBarStyled $isStampClockRoute={isStampClock}>
        {companyState.company ? (
          <NavbarMenu
            items={navbarItemsWithBadges}
            activeValue={activeMenuValue}
            onItemClick={(item, event) => {
              event.preventDefault();
              history.push(item.href);
            }}
          />
        ) : (
          <Spacer />
        )}

        <ActionsWrapper>
          {authState.signedIn && !isStampClock && hasChatModule && (
            <ChatBlob
              onMouseEnter={() => setIsChatContainerVisible(true)}
              onMouseLeave={() => setIsChatContainerVisible(false)}
            >
              <ChatIconWrapper>
                <CustomIcon
                  name="v2/chat"
                  color={ColorV2.Blue1}
                  padding="5px"
                />
                {missedMessagesQty > 0 && <Badge>{missedMessagesQty}</Badge>}
              </ChatIconWrapper>
              {isChatContainerVisible && (
                <ChatContainerList
                  setShowChatContainer={setIsChatContainerVisible}
                />
              )}
            </ChatBlob>
          )}

          {authState.signedIn ? (
            <DropdownMenu
              companyName={companyState.company?.companyName || ""}
              items={dropdownItems}
              isOpen={isProfileMenuOpen}
              onToggle={setIsProfileMenuOpen}
              onItemClick={(item, event) => {
                event.preventDefault();
                if (item.value === "signout") {
                  signOut(() => props.setOpen(false));
                } else if (item.value === "staff-register-export") {
                  setIsStampClockStaffRegisterModalOpen(true);
                } else if (item.value.startsWith("switch-")) {
                  const companyId = item.value.replace("switch-", "");
                  const company = companyState.companies.find(
                    (c) => c.id.toString() === companyId,
                  );
                  if (company) {
                    companyActions.switchCompany(company);
                  }
                } else if (item.value === "companies") {
                  history.push(item.href);
                } else {
                  history.push(item.href);
                }
                setIsProfileMenuOpen(false);
              }}
              avatarSrc={companyState.companyImage || ""}
            />
          ) : (
            <LoginLink to="/company/login">
              <Text>{t("LoginCompany.CompanyLogin")}</Text>
            </LoginLink>
          )}
        </ActionsWrapper>
      </NavBarStyled>
      <StampClockStaffRegisterExportModal
        companyId={companyState.company?.id}
        isOpen={isStampClockStaffRegisterModalOpen}
        onClose={() => setIsStampClockStaffRegisterModalOpen(false)}
      />
    </NavBarFrame>
  );
};
